// EchoCodesMacroButton.js - Electron-only macro button for auto-inserting codes

import React, { useState, useEffect, useCallback } from 'react';
import './EchoCodesMacroButton.css';
import { getCachedCodeMap } from './echo-codes/echoCodesLoader';
import { getModifiersForEnumSet } from './echo-codes/modifier-enums.ts';
import { sortCodesByPriority } from './echo-codes/codeOrderingConfig';

/**
 * Macro button that auto-types all echo codes using @nut-tree/nut-js
 * Only visible in Electron environment
 */
const EchoCodesMacroButton = ({ codes }) => {
  const [showModal, setShowModal] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const [error, setError] = useState(null);

  // Check if running in Electron
  const isElectron = window.isElectron === true && window.electronAPI !== undefined;

  // Flatten all codes into a single array with category info
  const getAllCodesFlat = useCallback(() => {
    const flatCodes = [];

    // Order of categories to process
    const categoryOrder = [
      'vg', 'vd', 'oreillettes', 'valve_mitrale', 'valve_tricuspide',
      'valve_aortique', 'valve_pulmonaire', 'gros_vaisseaux', 'pericarde', 'divers'
    ];

    categoryOrder.forEach(category => {
      const categoryCodes = codes[category] || [];
      // Sort codes by priority order before flattening
      const sortedCodes = sortCodesByPriority(categoryCodes, category);
      sortedCodes.forEach(codeObj => {
        flatCodes.push({
          code: codeObj.code,
          modifier: codeObj.modifier,
          value: codeObj.value,
          category: category
        });
      });
    });

    return flatCodes;
  }, [codes]);

  // Handle cancel
  const handleCancel = useCallback(() => {
    setShowModal(false);
    setIsExecuting(false);
    setError(null);
    // Notify Electron to cancel if executing
    if (isExecuting && window.electronAPI?.cancelMacro) {
      window.electronAPI.cancelMacro();
    }
  }, [isExecuting]);

  // Start macro execution
  const startMacroExecution = useCallback(async () => {
    setIsExecuting(true);
    setError(null);

    const flatCodes = getAllCodesFlat();

    if (flatCodes.length === 0) {
      setError('Aucun code à insérer');
      setIsExecuting(false);
      return;
    }

    try {
      // Get cached code map for looking up modifier enums
      const codeMap = getCachedCodeMap();

      // Build macro instructions for Electron
      const macroInstructions = flatCodes.map(codeObj => {
        // Get the full code details to access modifierEnumType
        const codeDetails = codeMap.get(codeObj.code);
        const hasValue = codeObj.value !== null && codeObj.value !== undefined;
        const hasModifier = !!codeObj.modifier;

        const instruction = {
          code: codeObj.code,
          modifier: null,
          value: null,
          modifierPosition: -1
        };

        // For codes with modifierEnumType, use modifier field to find position in enum
        // Generate DOWN sequences based on position - ignore value field entirely
        if (codeDetails?.modifierEnumType) {
          const modifiers = getModifiersForEnumSet(codeDetails.modifierEnumType);
          let modifierPosition = 0; // Default to first option

          if (hasModifier) {
            // Find position of modifier in the enum array
            modifierPosition = modifiers.findIndex(
              m => m.toLowerCase() === codeObj.modifier.toLowerCase()
            );
            if (modifierPosition < 0) modifierPosition = 0; // Fallback to first if not found
          }

          instruction.modifier = codeObj.modifier || modifiers[0];
          instruction.modifierPosition = modifierPosition;
        } else {
          // Non-enum code - use modifier/value as-is
          instruction.modifier = codeObj.modifier || null;
          instruction.value = hasValue ? codeObj.value : null;
        }

        return instruction;
      });

      // Send to Electron for execution
      const result = await window.electronAPI.executeMacro(macroInstructions);

      if (result.success) {
        setShowModal(false);
        setIsExecuting(false);
      } else {
        setError(result.error || 'Erreur lors de l\'exécution');
        setIsExecuting(false);
      }
    } catch (err) {
      console.error('Macro execution error:', err);
      setError(err.message || 'Erreur inconnue');
      setIsExecuting(false);
    }
  }, [getAllCodesFlat]);

  // Register/unregister global shortcuts when modal opens/closes
  useEffect(() => {
    if (!isElectron || !showModal) return;

    // Register global shortcuts
    window.electronAPI.registerMacroShortcuts();

    // Listen for shortcut events from Electron
    const unsubscribeStart = window.electronAPI.onMacroStart(() => {
      if (!isExecuting) {
        startMacroExecution();
      }
    });

    const unsubscribeCancel = window.electronAPI.onMacroCancel(() => {
      handleCancel();
    });

    // Cleanup: unregister shortcuts when modal closes
    return () => {
      window.electronAPI.unregisterMacroShortcuts();
      unsubscribeStart();
      unsubscribeCancel();
    };
  }, [showModal, isExecuting, isElectron, startMacroExecution, handleCancel]);

  // Handle button click
  const handleClick = () => {
    const flatCodes = getAllCodesFlat();
    if (flatCodes.length === 0) {
      setError('Aucun code à insérer');
      return;
    }
    setShowModal(true);
    setError(null);
  };

  // Don't render if not in Electron (after all hooks)
  if (!isElectron) {
    return null;
  }

  return (
    <>
      <button
        className="echo-macro-button"
        onClick={handleClick}
        disabled={isExecuting}
        title="Insérer automatiquement tous les codes"
      >
        {isExecuting ? '⏸ En cours...' : '⚡ Insertion automatique'}
      </button>

      {showModal && (
        <div className="echo-macro-modal-overlay">
          <div className="echo-macro-modal">
            <div className="echo-macro-modal-header">
              <h3>Insertion automatique</h3>
              <button
                className="echo-macro-modal-close"
                onClick={handleCancel}
              >
                ✕
              </button>
            </div>

            <div className="echo-macro-modal-body">
              {!isExecuting ? (
                <>
                  <p className="echo-macro-info">
                    📋 <strong>{getAllCodesFlat().length} codes</strong> seront insérés automatiquement
                  </p>
                  <p className="echo-macro-instructions">
                    1. Cliquez dans le champ de saisie du code<br />
                    2. Appuyez sur <kbd>Entrée</kbd> pour démarrer<br />
                    3. Appuyez sur <kbd>Échap</kbd> pour annuler
                  </p>
                  {error && (
                    <p className="echo-macro-error">{error}</p>
                  )}
                </>
              ) : (
                <>
                  <p className="echo-macro-executing">
                    ⏳ Insertion en cours...
                  </p>
                  <p className="echo-macro-instructions">
                    Appuyez sur <kbd>Échap</kbd> pour annuler
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EchoCodesMacroButton;
